import { type QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import type { GridApi, IServerSideGetRowsParams } from 'ag-grid-enterprise';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { atom, useAtomValue } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useEffect, useMemo } from 'react';
import { createSearchParams } from 'react-router';

import type {
  QBFilter,
  QBFilterDefinitionsResponseDTO,
  QBFilterOptions
} from '@/components/QueryBuilder';
import { QBOperator } from '@/components/QueryBuilder/QueryBuilder.constants';
import {
  qbFiltersAtom,
  qbTimeFilterAtom
} from '@/components/QueryBuilder/QueryBuilder.hooks';
import {
  mapQBFilterDefinitionsToConfig,
  mapQBFiltersToAPIParams,
  mapQBGroupByOptions
} from '@/components/QueryBuilder/QueryBuilder.utils';
import {
  DEFAULT_TIME_FILTER,
  parseTimeFilter,
  type TimePresetKeyWithField
} from '@/components/TimeFilter';
import {
  DEFAULT_PAGE_SIZE,
  PONDURANCE_NAME,
  TICKET_FILTERS_STORAGE_KEY,
  TICKET_TIME_FILTER_STORAGE_KEY
} from '@/constants/common';
import type { SearchFilters } from '@/features/SearchBar';
import useHandleError from '@/hooks/useHandleError';
import { axiosClient, queryClient } from '@/services/BaseApi.service';
import type { Order } from '@/types/common';

import dashboardTimeFrameAtom from '../Dashboard/Dashboard.store';
import { selectedOrganizationAtom } from '../Organizations';
import {
  TICKET_STATUS,
  TICKETS_SEARCH_FIELD_LABELS
} from './Tickets.constants';
import type {
  Ticket,
  TicketCategoryDTO,
  TicketExport,
  TicketExportInternal,
  TicketExportPayload,
  TicketPriorityDTO,
  TicketSearchResponse,
  TicketStatusDTO
} from './Tickets.types';
import { mapTicketsDTO } from './Tickets.utils';

export const exportedTicketsAtom = atom<
  TicketExport[] | TicketExportInternal[]
>([]);
export const exportedTicketsColumnSelectionAtom = atomWithStorage<string[]>(
  'TicketTablesExportColumns',
  []
);
export const orgOnlyTicketsAtom = atom<Ticket[]>([]);
export const ticketsTablePageAtom = atom(0);
export const openTicketsCountAtom = atom<number>(0);
export const ticketsTableRowsAtom = atomWithStorage(
  'TicketTablesRows',
  DEFAULT_PAGE_SIZE
);
export const ticketsTableOrderAtom = atomWithStorage<Order>(
  'TicketsTableOrder',
  'asc'
);
export const ticketsTableOrderByAtom = atomWithStorage<string>(
  'TicketsTableOrderBy',
  'id'
);

export const ticketStatusesAtom = atom<TicketStatusDTO[]>([]);
export const ticketPrioritiesAtom = atom<TicketPriorityDTO[]>([]);
export const ticketCategoriesAtom = atom<TicketCategoryDTO[]>([]);

const createJotaiSessionStorage = <Value>() =>
  createJSONStorage<Value>(() => sessionStorage);

export const ticketFiltersAtom = atomWithStorage<SearchFilters | null>(
  TICKET_FILTERS_STORAGE_KEY,
  null,
  createJotaiSessionStorage<SearchFilters | null>()
);
export const ticketTimeFilterAtom = atomWithStorage<TimePresetKeyWithField>(
  TICKET_TIME_FILTER_STORAGE_KEY,
  DEFAULT_TIME_FILTER,
  createJotaiSessionStorage<TimePresetKeyWithField>()
);
export const ticketWidgetsCollapsedAtom = atomWithStorage<boolean>(
  'ticketWidgetsCollapsed',
  false
);

export const ticketsGridApiAtom = atom<GridApi | null>(null);

// ensure default ticket filters are set in Session Storage
if (!sessionStorage.getItem(TICKET_FILTERS_STORAGE_KEY)) {
  sessionStorage.setItem(
    TICKET_FILTERS_STORAGE_KEY,
    JSON.stringify({
      status_name: [TICKET_STATUS.OPEN, TICKET_STATUS.ACKNOWLEDGED]
    })
  );
}

// generic method to fetch tickets with the new search endpoint. Error handling is left to the caller
// since the search params can be used in multiple places and the error handling might differ based on the use case
type FetchV2TicketsProps = {
  enabled: boolean;
  searchParams?: Record<string, unknown>;
  queryParams?: URLSearchParams | string;
  refetchIntervalMs?: number;
  fetchAllPages?: boolean;
};

export const useFetchV2Tickets = ({
  enabled,
  searchParams,
  queryParams,
  refetchIntervalMs,
  fetchAllPages = false
}: FetchV2TicketsProps) => {
  const defaultQueryParams = createSearchParams({
    page: '1',
    page_size: '1000'
  });

  const { pathname } = window.location;
  const isInternalTicketsPage = pathname.includes('/dashboard/internal');
  const finalQueryParams = queryParams ?? defaultQueryParams;
  const queryParamsString = finalQueryParams.toString();
  const handleError = useHandleError();
  const requestBody = searchParams ?? {
    published_at__gte: '2016-04-04 00:00:00'
  };

  const queryFn = async ({ signal }: { signal: AbortSignal }) => {
    const fetchPage = async (params: string) => {
      const response = await axiosClient.post<TicketSearchResponse>(
        `/v2/tickets/search?${params}`,
        requestBody,
        { signal }
      );
      return response.data;
    };

    const response = await fetchPage(queryParamsString);
    if (!fetchAllPages) {
      return response;
    }

    const tickets = [...response.data];
    const params = new URLSearchParams(queryParamsString);
    for (
      let page = response.pagination.page + 1;
      page <= response.pagination.total_pages;
      page++
    ) {
      params.set('page', page.toString());
      const nextPage = await fetchPage(params.toString());
      tickets.push(...nextPage.data);
    }

    return { ...response, data: tickets };
  };

  const { data, isFetching, isError, error } = useQuery({
    queryKey: [
      'v2/tickets/search',
      queryParamsString,
      JSON.stringify(requestBody),
      ...(fetchAllPages ? ['all-pages'] : [])
    ],
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
    select: (responseData: TicketSearchResponse) => ({
      data: mapTicketsDTO(responseData.data),
      pagination: responseData.pagination
    }),
    refetchInterval: refetchIntervalMs,
    refetchIntervalInBackground: isInternalTicketsPage
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch tickets.');
    }
  }, [isError, error, handleError]);

  return {
    data: data?.data ?? [],
    pagination: data?.pagination ?? {},
    isFetching
  };
};

export const useFetchTicketEntities = () => {
  const handleError = useHandleError();

  const { data, isError, error, isLoading } = useQuery<
    Record<string, QBFilterOptions[]>
  >({
    queryKey: ['ticket_entities/'],
    staleTime: Infinity
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch ticket entities configuration.'
      );
    }
  }, [isError, error]);

  return { data, isLoading };
};

export const useFetchTicketFilters = () => {
  const handleError = useHandleError();

  const { data, isError, error, isLoading } = useQuery({
    queryKey: ['v2/tickets/filters'],
    staleTime: Infinity,
    select: (data) =>
      mapQBFilterDefinitionsToConfig(
        data as QBFilterDefinitionsResponseDTO,
        TICKETS_SEARCH_FIELD_LABELS
      )
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch ticket filters configuration.'
      );
    }
  }, [isError, error]);

  return { data, isLoading };
};

export const mapFiltersToRequestParams = (
  basicFilters: QBFilter[],
  timeFilter?: TimePresetKeyWithField | null,
  timeFilterField?: string,
  updatedTimeFilterField?: string
) => {
  const params: Record<string, string> = {};
  if (basicFilters) {
    Object.entries(mapQBFiltersToAPIParams(basicFilters)).forEach(
      ([key, value]) => {
        const normalizedValues = String(value)
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        const isPonduranceOnlyAuthorFilter =
          key.includes('user__name') &&
          normalizedValues.length === 1 &&
          normalizedValues[0] === PONDURANCE_NAME;
        if (isPonduranceOnlyAuthorFilter) {
          const paramValue =
            !key.includes(QBOperator.Neq) && !key.includes(QBOperator.NotIn);
          params.user__internal = paramValue ? 'true' : 'false';
        } else {
          params[key] = value as string;
        }
      }
    );
  }

  if (timeFilter) {
    const { from, to, column } = parseTimeFilter(timeFilter);
    const cTimeFilterField = timeFilterField ?? 'created_at';
    const uTimeFilterField = updatedTimeFilterField ?? 'updated_at';
    const effectiveTimeFilterField =
      column === 'C' ? cTimeFilterField : uTimeFilterField;
    if (from) params[`${effectiveTimeFilterField}__gte`] = from.toString();
    if (to) params[`${effectiveTimeFilterField}__lte`] = to.toString();
  }

  return params;
};

export const useFetchGroupByOptions = (
  groups: string[],
  customFilters?: Record<string, string | string[] | number | number[]>,
  setValue?: (value: Record<string, QBFilterOptions[]>) => void,
  enabled = true
) => {
  const handleError = useHandleError();
  const ticketFilters = useAtomValue(qbFiltersAtom);
  const ticketTimeFilter = useAtomValue(qbTimeFilterAtom);

  const requestParams = useMemo(() => {
    if (customFilters) {
      return { ...customFilters, groups };
    }
    const params = mapFiltersToRequestParams(
      ticketFilters || [],
      ticketTimeFilter,
      'published_at'
    );
    return { ...params, groups };
  }, [ticketFilters, ticketTimeFilter, groups, customFilters]);

  const { data, isError, error, isLoading, isFetching, isSuccess } = useQuery({
    queryKey: ['v2/tickets/group_by', requestParams],
    queryFn: () =>
      axiosClient
        .post(`/v2/tickets/group_by?page=1&page_size=100`, requestParams)
        .then((resp) => resp.data),
    select: mapQBGroupByOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled:
      enabled &&
      (customFilters !== undefined ||
        (ticketFilters !== null && ticketTimeFilter !== null))
  });

  useEffect(() => {
    if (isSuccess && setValue) {
      setValue(data || {});
    }
  }, [data, isSuccess, setValue]);

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch group by options for tickets.'
      );
    }
  }, [isError, error, handleError]);

  return { data, isLoading, isFetching };
};

export const useFetchThreatTicketsByDate = () => {
  const handleError = useHandleError();
  const dashboardTimeFrame = useAtomValue(dashboardTimeFrameAtom);
  const selectedOrg = useAtomValue(selectedOrganizationAtom);

  const startDate = dayjs()
    .subtract(dashboardTimeFrame + 1, 'day')
    .startOf('day')
    .format('YYYY-MM-DD');

  const endDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

  const params =
    selectedOrg.id === 0
      ? createSearchParams({
          start_date: startDate,
          end_date: endDate
        }).toString()
      : createSearchParams({
          start_date: startDate,
          end_date: endDate,
          organization_id: selectedOrg.id.toString()
        }).toString();

  const { data, isError, error, isLoading, isFetching } = useQuery({
    queryKey: ['v2/tickets/group_by_date_priority', params],
    queryFn: () =>
      axiosClient
        .get(`/v2/tickets/group_by_date_priority?${params}`)
        .then((r) => r.data),
    staleTime: Infinity,
    select: (resp) => {
      return resp.map((item: { [key: string]: string | number }) => ({
        ...item,
        date: dayjs(item.date).toDate()
      }));
    }
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch threat tickets by date.'
      );
    }
  }, [isError, error, handleError]);

  return { data, isLoading, isFetching };
};

export const clearTicketsSearchCache = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['v2/tickets/search'] });
  queryClient.invalidateQueries({ queryKey: ['v2/tickets/group_by'] });
};

// Module-level so results survive datasource recreation (e.g. leaving and returning to the page)
// for the lifetime of the SPA session, keyed by the full request signature.
export const getServerSideDatasource = (
  organizationLabel: string,
  ticketFilters?: QBFilter[] | null,
  ticketTimeFilter?: TimePresetKeyWithField,
  handleError?: (errorResponse: AxiosError, errorMessage: string) => void,
  onLoadingChange?: (isFetching: boolean) => void
) => {
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      onLoadingChange?.(true);
      try {
        if (!ticketTimeFilter) {
          params.success({ rowData: [], rowCount: 0 });
          return;
        }

        const { startRow, sortModel } = params.request;
        const pageSize = params.api.paginationGetPageSize();
        const pagesToFetch = pageSize * 5;

        // Build query parameters
        const queryParams = new URLSearchParams({
          page: ((startRow as number) / pagesToFetch + 1).toString(), // Calculate page number based on startRow and pagesToFetch
          page_size: pagesToFetch.toString()
        });

        const requestParams = mapFiltersToRequestParams(
          ticketFilters || [],
          ticketTimeFilter,
          'published_at'
        );

        // Add organization filter if it is not the all organizations option
        if (organizationLabel !== 'all' && organizationLabel !== 'noop') {
          requestParams.organization__label = organizationLabel;
        }

        // Add sorting if specified
        if (sortModel && sortModel.length > 0) {
          let { colId } = sortModel[0];
          const { sort } = sortModel[0];
          const sortPrefix = sort === 'asc' ? '' : '-';
          if (colId === 'organization_name') {
            colId = 'organization__name';
          }
          requestParams.order_by = `${sortPrefix}${colId}`;
        }

        if (
          organizationLabel !== 'all' &&
          organizationLabel !== 'noop' &&
          requestParams.order_by === 'organization__name'
        ) {
          delete requestParams.order_by; // Remove primary sorting by organization name when filtering by specific organization
        }

        const response = await queryClient.fetchQuery({
          queryKey: [
            'v2/tickets/search',
            Object.fromEntries(queryParams),
            requestParams
          ],
          queryFn: () =>
            axiosClient
              .post(
                `/v2/tickets/search?${queryParams.toString()}`,
                requestParams
              )
              .then((resp) => resp.data),
          staleTime: 5 * 60 * 1000
        });
        const result = {
          rowData: mapTicketsDTO(response.data) || [],
          rowCount: response.pagination.total || 0
        };

        params.success(result);
      } catch (error) {
        console.error('Server-side datasource error:', error);
        params.fail();

        if (handleError) {
          const axiosError =
            error instanceof AxiosError
              ? error
              : new AxiosError(
                  error instanceof Error ? error.message : String(error)
                );

          handleError(axiosError, 'Failure to fetch tickets.');
        }
      } finally {
        onLoadingChange?.(false);
      }
    }
  };
};

export const useFetchFilteredTicketGroups = (
  groups: string[],
  customFilters?: Record<string, string | string[] | number | number[]>,
  enabled = true
) => {
  const handleError = useHandleError();
  const ticketFilters = useAtomValue(qbFiltersAtom);
  const ticketTimeFilter = useAtomValue(qbTimeFilterAtom);

  const requestParams = useMemo(() => {
    if (customFilters) {
      return { ...customFilters, groups };
    }
    const params = mapFiltersToRequestParams(
      ticketFilters || [],
      ticketTimeFilter,
      'published_at'
    );
    return { ...params, groups };
  }, [ticketFilters, ticketTimeFilter, groups, customFilters]);

  const { data, isError, error, isLoading, isFetching } = useQuery({
    queryKey: ['v2/tickets/group_by', requestParams],
    queryFn: () =>
      axiosClient
        .post(`/v2/tickets/group_by?page=1&page_size=100`, requestParams)
        .then((resp) => resp.data),
    select: mapQBGroupByOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled:
      enabled &&
      (customFilters !== undefined ||
        (ticketFilters !== null && ticketTimeFilter !== null))
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch group by options for tickets.'
      );
    }
  }, [isError, error]);

  return { data, isLoading, isFetching };
};

export const useFetchTicketExport = () => {
  return useMutation({
    mutationFn: ({ format, columns, ticketFilters }: TicketExportPayload) => {
      const params = new URLSearchParams({
        export_format: format,
        columns: columns?.join(',') || ''
      });
      return axiosClient.post('/v2/tickets/export', ticketFilters || {}, {
        params,
        responseType: 'blob'
      });
    }
  });
};
